import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import '../parents/AllParents.css';
import LocationFilter from '../locaties/LocationFilter.js';
import LifecycleActionButtons from '../../components/LifecycleActionButtons.js';
import {
  confirmLifecycleAction,
  showLifecycleSuccess,
  showLifecycleError
} from '../../utils/confirmLifecycleAction.js';
import { getStatusBadgeInfo } from '../../utils/lifecycleUtils.js';
function AllParents() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('jwtToken');
  const { userInfo, isAuthenticated } = useAuth();
  const role = userInfo?.role;
  const is_single_user = userInfo?.is_single_user;
  const [copyFeedback, setCopyFeedback] = useState(null);
  const [parents, setParents] = useState([]);
  const [zoekterm, setZoekterm] = useState('');
  const [debouncedZoekterm, setDebouncedZoekterm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [locationFilter, setLocationFilter] = useState('all');
  const [showInactive, setShowInactive] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false
  });
  axios.defaults.withCredentials = true;
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedZoekterm(zoekterm.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [zoekterm]);
  useEffect(() => {
    if (!copyFeedback) return undefined;
    const timer = setTimeout(() => setCopyFeedback(null), 2500);
    return () => clearTimeout(timer);
  }, [copyFeedback]);

  const handleCopyEmail = async (event, parent) => {
    event.stopPropagation();
    const email = parent.email?.trim();
    if (!email) return;

    try {
      await navigator.clipboard.writeText(email);
      setCopyFeedback({ parentId: parent.parent_id, success: true });
    } catch {
      setCopyFeedback({ parentId: parent.parent_id, success: false });
    }
  };

  const fetchParents = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/parent/allparents`,
        {
          params: {
            page,
            limit,
            frontend_location_id:
              role === 'super_admin' && !is_single_user
                ? locationFilter
                : undefined,
            search: debouncedZoekterm || undefined,
            showInactive,
            showArchived
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setParents(response.data.data || []);
      setPagination(
        response.data.pagination || {
          page: 1,
          limit,
          total: 0,
          totalPages: 1,
          hasNextPage: false,
          hasPrevPage: false
        }
      );
    } catch (error) {
      console.error('Error fetching parents', error);
      setParents([]);
      setPagination({
        page: 1,
        limit,
        total: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false
      });
    } finally {
      setIsLoading(false);
    }
  }, [token, role, is_single_user, locationFilter, debouncedZoekterm, showInactive, showArchived, page, limit]);
  const getLocationName = (id) => {
    if (!id) return 'Geen locatie';
    const loc = locations.find((l) => l.location_id === id);
    return loc ? loc.location_name : id;
  };
  useEffect(() => {
    fetchParents();
  }, [fetchParents]);
  useEffect(() => {
    if (role === 'super_admin') {
      axios
        .get(`${process.env.REACT_APP_BACKEND_URL}/location/allLocations`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        .then((res) => {
          const locs = res.data.data || res.data;
          setLocations(Array.isArray(locs) ? locs : []);
        })
        .catch((err) => console.error('Error fetching locations', err));
    } else {
      setLocations([]);
    }
  }, [role, token]);
  const handleLifecycleAction = async (parentId, action, entityLabel) => {
    const { isConfirmed } = await confirmLifecycleAction(action, entityLabel);
    if (!isConfirmed) return;
    setActionLoadingId(parentId);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/parent/${parentId}/${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await showLifecycleSuccess(
        action,
        entityLabel,
        res.data?.message || ''
      );
      await fetchParents();
    } catch (err) {
      console.error(`Fout bij lifecycle parent action ${action}`, err);
      await showLifecycleError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          `Actie mislukt: ${action}`
      );
    } finally {
      setActionLoadingId(null);
    }
  };
  const getStatusBadge = (parent) => {
    const badge = getStatusBadgeInfo(parent);
    return <span className={badge.className}>{badge.label}</span>;
  };
  const handleView = (id) => navigate(`/ouder/view/${id}`);
  const showLocationColumn = role === 'super_admin' && !is_single_user;
  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1);
  };
  const renderPaginationButtons = () => {
    if ((pagination.totalPages || 1) <= 1) return null;
    const currentPage = pagination.page || 1;
    const totalPages = pagination.totalPages || 1;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);
    if (currentPage <= 2) {
      endPage = Math.min(totalPages, 5);
    }
    if (currentPage >= totalPages - 1) {
      startPage = Math.max(1, totalPages - 4);
    }
    const buttons = [];
    for (let i = startPage; i <= endPage; i += 1) {
      buttons.push(
        <button
          key={i}
          type="button"
          className={`btn btn-sm ${i === currentPage ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => setPage(i)}
        >
          {i}
        </button>
      );
    }
    return buttons;
  };
  const startRecord = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const endRecord =
    pagination.total === 0
      ? 0
      : Math.min(pagination.page * pagination.limit, pagination.total);
  return (
    <div className="all-parents">
      {isAuthenticated ? (
        <>
          <h1>Overzicht Van Alle Ouders</h1>
          <div className="d-flex justify-content-end mb-3">
            <Link to="/ouder/nieuw" className="btn btn-success">
              <i className="bi bi-plus-circle me-2"></i>Nieuwe ouder
            </Link>
          </div>
          {showLocationColumn && (
            <div className="mb-3">
              <LocationFilter
                userRole={role}
                locationFilter={locationFilter}
                setLocationFilter={(value) => {
                  setLocationFilter(value);
                  setPage(1);
                }}
                locations={locations}
              />
            </div>
          )}
          <div className="row mb-3">
            <div className="col-12 col-md-4 mb-2">
              <input
                type="text"
                className="form-control rounded-0"
                placeholder="Zoek op naam, email of mobiel"
                value={zoekterm}
                onChange={(e) => {
                  setZoekterm(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <div className="col-12 col-md-2 mb-2">
              <select
                className="form-select rounded-0"
                value={limit}
                onChange={handleLimitChange}
              >
                <option value={10}>Toon 10</option>
                <option value={20}>Toon 20</option>
                <option value={50}>Toon 50</option>
                <option value={100}>Toon 100</option>
              </select>
            </div>
            <div className="col-12 col-md-6 d-flex flex-column flex-md-row gap-3 align-items-md-center">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="showInactiveParents"
                  checked={showInactive}
                  onChange={(e) => {
                    setShowInactive(e.target.checked);
                    setPage(1);
                  }}
                />
                <label className="form-check-label" htmlFor="showInactiveParents">
                  Toon inactieve ouders
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="showArchivedParents"
                  checked={showArchived}
                  onChange={(e) => {
                    setShowArchived(e.target.checked);
                    setPage(1);
                  }}
                />
                <label className="form-check-label" htmlFor="showArchivedParents">
                  Toon gearchiveerde ouders
                </label>
              </div>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  setZoekterm('');
                  setLocationFilter('all');
                  setShowInactive(false);
                  setShowArchived(false);
                  setLimit(20);
                  setPage(1);
                }}
              >
                Filters wissen
              </button>
            </div>
          </div>
          <div className="row mb-3 align-items-center">
            <div className="col-12">
              <small className="text-muted">
                Toont {startRecord} t/m {endRecord} van {pagination.total || 0} ouders
              </small>
            </div>
          </div>
          {isLoading ? (
            <p>Loading...</p>
          ) : (
            <>
              <div className="parent-cards">
                {parents.length === 0 && (
                  <p className="text-center text-muted mt-3">Geen ouders gevonden.</p>
                )}
                {parents.map((parent) => (
                  <div
                    key={parent.parent_id}
                    className="parent-card"
                    onClick={() => handleView(parent.parent_id)}
                  >
                    <div className="parent-card-header">
                      <div className="parent-card-info">
                        <div className="parent-card-name">
                          {parent.firstname} {parent.lastname}
                        </div>
                        <div
                          className="parent-card-email d-flex align-items-center gap-2"
                          style={{ minWidth: 0 }}
                        >
                          <span style={{ overflowWrap: 'anywhere', minWidth: 0 }}>
                            {parent.email}
                          </span>
                          {parent.email?.trim() && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              style={{ flexShrink: 0 }}
                              onClick={(event) => handleCopyEmail(event, parent)}
                              onKeyDown={(event) => event.stopPropagation()}
                              title="E-mailadres kopiëren"
                              aria-label={`E-mailadres van ${parent.firstname} ${parent.lastname} kopiëren`}
                            >
                              <i
                                className={
                                  copyFeedback?.parentId === parent.parent_id && copyFeedback.success
                                    ? 'bi bi-check-lg text-success'
                                    : 'bi bi-clipboard'
                                }
                                aria-hidden="true"
                              />
                            </button>
                          )}
                        </div>
                        <span role="status" aria-live="polite" aria-atomic="true">
                          {copyFeedback?.parentId === parent.parent_id && (
                            <small className={copyFeedback.success ? 'text-success' : 'text-danger'}>
                              {copyFeedback.success
                                ? 'Gekopieerd'
                                : 'Kopiëren mislukt. Selecteer en kopieer het e-mailadres handmatig.'}
                            </small>
                          )}
                        </span>
                        <div className="mt-1">{getStatusBadge(parent)}</div>
                      </div>
                      {parent.mobile && (
                        <a
                          href={`tel:${parent.mobile}`}
                          className="btn btn-sm btn-outline-secondary rounded-circle"
                          style={{
                            width: 34,
                            height: 34,
                            padding: 0,
                            lineHeight: '32px',
                            textAlign: 'center',
                            flexShrink: 0
                          }}
                          onClick={(e) => e.stopPropagation()}
                          title={parent.mobile}
                        >
                          <i className="bi bi-telephone" style={{ fontSize: '13px' }}></i>
                        </a>
                      )}
                    </div>
                    {(parent.beroep || showLocationColumn) && (
                      <div className="parent-card-meta">
                        {parent.beroep && (
                          <span>
                            <i className="bi bi-briefcase me-1"></i>
                            {parent.beroep}
                          </span>
                        )}
                        {showLocationColumn && (
                          <span>
                            <i className="bi bi-geo-alt me-1"></i>
                            {getLocationName(parent.location_id) || '—'}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="parent-card-actions" onClick={(e) => e.stopPropagation()}>
                      <LifecycleActionButtons
                        item={parent}
                        itemId={parent.parent_id}
                        entityLabel="ouder"
                        editPath={`/ouder/edit/${parent.parent_id}`}
                        loadingId={actionLoadingId}
                        onAction={handleLifecycleAction}
                        compact
                      />
                    </div>
                  </div>
                ))}
              </div>
              {(pagination.totalPages || 1) > 1 && (
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2 mt-4">
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      disabled={!pagination.hasPrevPage}
                      onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    >
                      Vorige
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      disabled={!pagination.hasNextPage}
                      onClick={() => setPage((prev) => prev + 1)}
                    >
                      Volgende
                    </button>
                  </div>
                  <div className="d-flex flex-wrap justify-content-center gap-2">
                    {renderPaginationButtons()}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <p>You are not authenticated.</p>
      )}
    </div>
  );
}
export default AllParents;
